import { useTranslation } from "@sagot/i18n";
import { Button, Field, Select, Switch } from "@sagot/ui";
import { useId, useState } from "react";
import { Gate, useCanManage } from "../panels/panel-kit";
import { ABSOLUTE_HOURS_CHOICES, ACCESS_MINUTES_CHOICES, IDLE_MINUTES_CHOICES } from "./launch-settings-model";
import { useSaveLaunchSettings, type LaunchSettings } from "./use-launch-settings";

const K = "launch.session";

interface Limits {
  access: number;
  idle: number;
  absolute: number;
  urlToken: boolean;
}

/** The choices plus the saved value, so a value set over the API (inside the bounds) still shows. */
function withCurrent(choices: readonly number[], current: number): number[] {
  return choices.includes(current) ? [...choices] : [...choices, current].sort((a, b) => a - b);
}

function LimitSelect({ label, value, options, unitKey, disabled, onChange, testId }: { label: string; value: number; options: number[]; unitKey: string; disabled: boolean; onChange: (next: number) => void; testId: string }) {
  const { t } = useTranslation();
  return (
    <Field label={label}>
      <Select value={String(value)} disabled={disabled} onChange={(event) => { onChange(Number(event.target.value)); }} data-testid={testId}>
        {options.map((option) => (
          <option key={option} value={option}>
            {t(unitKey, { count: option })}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/**
 * Secure launch "Session limits" card (mockup secure-launch pin 4, PID-002, PID-003): how long a launched customer's access
 * token lasts (5 to 60 minutes), the idle timeout (5 to 60 minutes) and the absolute maximum (1 to 12 hours), and the switch for
 * the older sign-in tokens carried in a URL. A change applies to launches made after it.
 */
export function SessionLimitsCard({ settings }: { settings: LaunchSettings }) {
  const { t } = useTranslation();
  const manage = useCanManage("customers.manage_launch");
  const saver = useSaveLaunchSettings(t(`${K}.saved`));
  const tokenId = useId();
  const saved: Limits = { access: settings.session.access_minutes, idle: settings.session.idle_minutes, absolute: settings.session.absolute_hours, urlToken: settings.url_token_signin };
  const [draft, setDraft] = useState<Limits | null>(null);
  const shown = draft ?? saved;
  const dirty = draft !== null && (draft.access !== saved.access || draft.idle !== saved.idle || draft.absolute !== saved.absolute || draft.urlToken !== saved.urlToken);
  const edit = (patch: Partial<Limits>) => {
    setDraft({ ...shown, ...patch });
    saver.clearFailure();
  };
  const reset = () => {
    setDraft(null);
    saver.clearFailure();
  };
  const save = async () => {
    const done = await saver.save({
      session: { access_minutes: shown.access, idle_minutes: shown.idle, absolute_hours: shown.absolute },
      url_token_signin: shown.urlToken,
    });
    if (done) setDraft(null);
  };
  return (
    <section className="card" aria-labelledby="session-title" data-testid="session-card">
      <div className="card-header">
        <div className="min-w-0">
          <h2 id="session-title" className="card-title">
            {t(`${K}.title`)}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.subtitle`)}</p>
        </div>
      </div>
      <div className="space-y-4 px-5 py-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <LimitSelect label={t(`${K}.access`)} value={shown.access} options={withCurrent(ACCESS_MINUTES_CHOICES, saved.access)} unitKey={`${K}.minutes`} disabled={!manage.allowed} onChange={(access) => { edit({ access }); }} testId="session-access" />
          <LimitSelect label={t(`${K}.idle`)} value={shown.idle} options={withCurrent(IDLE_MINUTES_CHOICES, saved.idle)} unitKey={`${K}.minutes`} disabled={!manage.allowed} onChange={(idle) => { edit({ idle }); }} testId="session-idle" />
          <LimitSelect label={t(`${K}.absolute`)} value={shown.absolute} options={withCurrent(ABSOLUTE_HOURS_CHOICES, saved.absolute)} unitKey={`${K}.hours`} disabled={!manage.allowed} onChange={(absolute) => { edit({ absolute }); }} testId="session-absolute" />
        </div>
        <p className="text-xs text-fg-muted">{t(`${K}.limitsHint`)}</p>
        <div className="flex items-start justify-between gap-4 border-t border-line pt-4">
          <label htmlFor={tokenId} className="min-w-0 text-sm">
            <span id={`${tokenId}-name`} className="font-semibold">{t(`${K}.urlTokenTitle`)}</span>
            <span id={`${tokenId}-desc`} className="block text-fg-muted">{t(`${K}.urlTokenHint`)}</span>
          </label>
          <Switch id={tokenId} aria-labelledby={`${tokenId}-name`} aria-describedby={`${tokenId}-desc`} checked={shown.urlToken} disabled={!manage.allowed} onCheckedChange={(urlToken) => { edit({ urlToken }); }} data-testid="session-url-token" />
        </div>
        {manage.allowed ? null : <p className="text-xs text-fg-muted" data-testid="session-read-only">{manage.sessionReadOnly ? manage.reason : t(`${K}.readOnly`)}</p>}
        {saver.failure ? (
          <p role="alert" className="text-xs font-semibold text-danger" data-testid="session-error">
            {saver.failure}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="secondary" disabled={!dirty || saver.busy} onClick={reset}>
          {t(`${K}.discard`)}
        </Button>
        <Gate reason={manage.reason}>
          <Button loading={saver.busy} disabled={!manage.allowed || !dirty} onClick={() => void save()}>
            {saver.busy ? t(`${K}.saving`) : t(`${K}.save`)}
          </Button>
        </Gate>
      </div>
    </section>
  );
}
